import { useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { SegmentedControl } from '@/components/ui/SegmentedControl';

// Types
interface BTDRating {
  ratingType: 'btd';
  model_key: string;
  rating: number;
  standardError: number;
  matches: number;
  wins: number;
  losses: number;
  ties: number;
  both_bad: number;
}

interface RatingSnapshot {
  voteCount: number;
  timestamp?: string;
  ratings: BTDRating[];
}

export type XAxisMode = 'votes' | 'date';

interface RatingEvolution {
  snapshots: RatingSnapshot[];
  interval: number;
  totalVotes: number;
}

interface Props {
  evolution: RatingEvolution;
  displayNames: Map<string, string>;
  defaultTopN?: number;
  defaultMultiplier?: number;
  xAxisMode?: XAxisMode;
  onXAxisModeChange?: (mode: XAxisMode) => void;
}

// Color palette for model lines
const COLORS = [
  '#8B2332', // Meridian Red
  '#47331F', // Brown
  '#2563eb', // Blue
  '#16a34a', // Green
  '#ea580c', // Orange
  '#9333ea', // Purple
  '#0891b2', // Cyan
  '#e11d48', // Rose
  '#7c3aed', // Violet
  '#059669', // Emerald
];

// Sub-components
function ModelChip({
  displayName,
  isSelected,
  color,
  onClick,
}: {
  displayName: string;
  isSelected: boolean;
  color: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`
        px-3 py-1.5 rounded-md text-xs cursor-pointer transition-all duration-150
        ${isSelected ? 'font-semibold' : 'font-normal'}
      `}
      style={{
        border: `1px solid ${isSelected ? color : '#E8E0D8'}`,
        background: isSelected ? `${color}15` : 'white',
        color: isSelected ? color : '#8B8580',
      }}
    >
      {displayName}
    </button>
  );
}

function ChartEmptyState({ interval }: { interval: number }) {
  return (
    <div className="p-8 text-center text-slate bg-white/50 border border-cream-dark rounded-xl">
      Not enough data to show evolution (need at least {interval} votes)
    </div>
  );
}

// Data transformation helpers
function transformSnapshotsToChartData(
  snapshots: RatingSnapshot[],
  topModels: string[],
  uncertaintyMultiplier: number
) {
  return snapshots
    .map(snapshot => {
      const dataPoint: Record<string, any> = {
        voteCount: snapshot.voteCount,
        timestamp: snapshot.timestamp
          ? new Date(snapshot.timestamp).getTime()
          : undefined,
      };

      snapshot.ratings.forEach(rating => {
        if (topModels.includes(rating.model_key)) {
          dataPoint[rating.model_key] = rating.rating;
          dataPoint[`${rating.model_key}_se`] = rating.standardError;
          dataPoint[`${rating.model_key}_matches`] = rating.matches;

          const maxCIRange = 500;
          const rawCILow =
            rating.rating - rating.standardError * uncertaintyMultiplier;
          const rawCIHigh =
            rating.rating + rating.standardError * uncertaintyMultiplier;

          dataPoint[`${rating.model_key}_ci_low`] = Math.max(
            rawCILow,
            rating.rating - maxCIRange
          );
          dataPoint[`${rating.model_key}_ci_high`] = Math.min(
            rawCIHigh,
            rating.rating + maxCIRange
          );
          dataPoint[`${rating.model_key}_ci_low_raw`] = rawCILow;
          dataPoint[`${rating.model_key}_ci_high_raw`] = rawCIHigh;
        }
      });

      return dataPoint;
    })
    .filter(dataPoint =>
      topModels.every(modelKey => dataPoint[modelKey] != null)
    );
}

function calculateYAxisDomain(
  chartData: Record<string, any>[],
  topModels: string[]
): [number, number] {
  const allValues: number[] = [];
  chartData.forEach(dataPoint => {
    topModels.forEach(modelKey => {
      const rating = dataPoint[modelKey];
      const ciLow = dataPoint[`${modelKey}_ci_low`];
      const ciHigh = dataPoint[`${modelKey}_ci_high`];
      if (rating != null) allValues.push(rating);
      if (ciLow != null) allValues.push(ciLow);
      if (ciHigh != null) allValues.push(ciHigh);
    });
  });

  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const range = maxValue - minValue;
  const padding = Math.max(range * 0.1, 20);
  return [Math.floor(minValue - padding), Math.ceil(maxValue + padding)];
}

// Tooltip formatter
function formatTooltipValue(
  value: any,
  name: string,
  props: any,
  displayNames: Map<string, string>
) {
  if (
    name.includes('_se') ||
    name.includes('_ci_') ||
    name.includes('_band') ||
    name.includes('_raw') ||
    name.includes('_matches')
  ) {
    return null;
  }

  const displayName = displayNames.get(name) || name;
  const se = props.payload[`${name}_se`];
  const matches = props.payload[`${name}_matches`];
  const ciLowRaw = props.payload[`${name}_ci_low_raw`];
  const ciHighRaw = props.payload[`${name}_ci_high_raw`];
  const ciLow = ciLowRaw ?? props.payload[`${name}_ci_low`];
  const ciHigh = ciHighRaw ?? props.payload[`${name}_ci_high`];

  return [
    <div key={name} className="leading-relaxed">
      <div className="font-semibold">{Number(value).toFixed(0)}</div>
      {se != null && (
        <div className="text-xs text-slate mt-0.5">
          {matches} matches • 95% CI: [{ciLow.toFixed(0)}, {ciHigh.toFixed(0)}]
          (SE: {se.toFixed(0)})
        </div>
      )}
    </div>,
    displayName,
  ];
}

function formatTooltipLabel(label: number, xAxisMode: XAxisMode): string {
  if (xAxisMode === 'date') {
    const date = new Date(label);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
  return `After ${label} votes`;
}

// Main component
export default function UncertaintyChart({
  evolution,
  displayNames,
  defaultTopN = 3,
  defaultMultiplier = 1.96,
  xAxisMode: controlledXAxisMode,
  onXAxisModeChange,
}: Props) {
  const [topN, setTopN] = useState(defaultTopN);
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [internalXAxisMode, setInternalXAxisMode] =
    useState<XAxisMode>('votes');

  const xAxisMode = controlledXAxisMode ?? internalXAxisMode;
  const setXAxisMode = (mode: XAxisMode) => {
    if (onXAxisModeChange) {
      onXAxisModeChange(mode);
    } else {
      setInternalXAxisMode(mode);
    }
  };

  const hasTimestamps = evolution.snapshots?.some(s => s.timestamp);

  if (!evolution.snapshots || evolution.snapshots.length === 0) {
    return <ChartEmptyState interval={evolution.interval} />;
  }

  // Determine which models to show
  const finalSnapshot = evolution.snapshots[evolution.snapshots.length - 1];
  const allModels = finalSnapshot.ratings
    .sort((a, b) => b.rating - a.rating)
    .map(r => r.model_key);

  const topModels =
    selectedModels.length > 0 ? selectedModels : allModels.slice(0, topN);

  // Filter early snapshots with high uncertainty
  const minVotesToShow = Math.max(20, evolution.interval * 2);
  const filteredSnapshots = evolution.snapshots.filter(
    s => s.voteCount >= minVotesToShow
  );
  const snapshotsToUse =
    filteredSnapshots.length > 0 ? filteredSnapshots : evolution.snapshots;

  // Transform data for chart
  const chartData = transformSnapshotsToChartData(
    snapshotsToUse,
    topModels,
    defaultMultiplier
  );
  const [yMin, yMax] = calculateYAxisDomain(chartData, topModels);

  const xAxisOptions = [
    { value: 'votes' as const, label: 'Votes' },
    { value: 'date' as const, label: 'Date' },
  ];

  return (
    <div className="bg-white border border-cream-dark rounded-xl p-6 mb-8">
      {/* Header */}
      <div className="mb-6">
        <h3 className="font-serif text-xl font-medium text-ink mb-4">
          Rating Evolution Over Time
        </h3>

        {/* Controls */}
        <div className="flex gap-4 items-center flex-wrap mb-4">
          <div className="flex gap-2 items-center">
            <label className="text-sm text-slate font-medium">Show:</label>
            <select
              value={topN}
              onChange={e => {
                setTopN(Number(e.target.value));
                setSelectedModels([]);
              }}
              disabled={selectedModels.length > 0}
              className={`
                px-2.5 py-1.5 border border-cream-dark rounded-md text-sm text-ink
                ${selectedModels.length > 0 ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'}
              `}
            >
              {[3, 5, 8].map(n => (
                <option key={n} value={n}>
                  Top {n}
                </option>
              ))}
            </select>
          </div>

          {hasTimestamps && (
            <div className="flex gap-2 items-center">
              <label className="text-sm text-slate font-medium">X-Axis:</label>
              <SegmentedControl
                options={xAxisOptions}
                value={xAxisMode}
                onChange={setXAxisMode}
                size="sm"
              />
            </div>
          )}

          {selectedModels.length > 0 && (
            <button
              onClick={() => setSelectedModels([])}
              className="px-3 py-1.5 border border-cream-dark rounded-md text-sm text-meridian bg-white cursor-pointer font-medium hover:bg-cream-light"
            >
              Reset Selection
            </button>
          )}
        </div>

        {/* Model chips */}
        <div className="flex gap-2 flex-wrap">
          <span className="text-sm text-slate font-medium self-center">
            Compare:
          </span>
          {allModels.slice(0, 10).map(modelKey => {
            const isSelected = topModels.includes(modelKey);
            const color = isSelected
              ? COLORS[topModels.indexOf(modelKey) % COLORS.length]
              : '#8B8580';
            return (
              <ModelChip
                key={modelKey}
                displayName={displayNames.get(modelKey) || modelKey}
                isSelected={isSelected}
                color={color}
                onClick={() => {
                  if (selectedModels.includes(modelKey)) {
                    setSelectedModels(
                      selectedModels.filter(m => m !== modelKey)
                    );
                  } else {
                    setSelectedModels([...selectedModels, modelKey]);
                  }
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={450}>
        <ComposedChart
          data={chartData}
          margin={{ top: 10, right: 20, left: 60, bottom: 30 }}
        >
          <defs>
            {topModels.map((modelKey, index) => {
              const color = COLORS[index % COLORS.length];
              return (
                <linearGradient
                  key={`gradient-${modelKey}`}
                  id={`gradient-${modelKey}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={color} stopOpacity={0.2} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.05} />
                </linearGradient>
              );
            })}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#E8E3DD" />
          <XAxis
            dataKey={xAxisMode === 'votes' ? 'voteCount' : 'timestamp'}
            label={{
              value: xAxisMode === 'votes' ? 'Total Number of Votes' : 'Date',
              position: 'insideBottom',
              offset: -10,
              style: { fill: '#8B8580', fontSize: '0.875rem' },
            }}
            stroke="#8B8580"
            style={{ fontSize: '0.875rem' }}
            domain={['dataMin', 'dataMax']}
            scale={xAxisMode === 'date' ? 'time' : 'auto'}
            type="number"
            tickFormatter={
              xAxisMode === 'date'
                ? (value: number) =>
                    new Date(value).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })
                : undefined
            }
          />
          <YAxis
            label={{
              value: 'Rating (Elo-scale)',
              angle: -90,
              position: 'insideLeft',
              style: { fill: '#8B8580', fontSize: '0.875rem' },
            }}
            stroke="#8B8580"
            style={{ fontSize: '0.875rem' }}
            domain={[yMin, yMax]}
          />
          <Tooltip
            contentStyle={{
              background: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid #E8E0D8',
              borderRadius: '8px',
              fontSize: '0.875rem',
              padding: '0.75rem',
            }}
            labelStyle={{
              color: '#1A1A1A',
              fontWeight: 500,
              marginBottom: '0.5rem',
            }}
            formatter={(value: any, name: string, props: any) =>
              formatTooltipValue(value, name, props, displayNames)
            }
            labelFormatter={label => formatTooltipLabel(label, xAxisMode)}
          />
          <Legend
            wrapperStyle={{ fontSize: '0.875rem', paddingTop: '20px' }}
            formatter={(value: string) => {
              if (
                value.includes('_se') ||
                value.includes('_ci_') ||
                value.includes('_band')
              ) {
                return null;
              }
              return displayNames.get(value) || value;
            }}
            layout="horizontal"
            verticalAlign="bottom"
            align="center"
          />

          {topModels.map((modelKey, index) => {
            const color = COLORS[index % COLORS.length];
            return (
              <g key={modelKey}>
                <Area
                  type="monotone"
                  dataKey={`${modelKey}_ci_high`}
                  stroke="none"
                  fill={`url(#gradient-${modelKey})`}
                  legendType="none"
                  name={`${modelKey}_band`}
                />
                <Area
                  type="monotone"
                  dataKey={`${modelKey}_ci_low`}
                  stroke="none"
                  fill="white"
                  legendType="none"
                  name={`${modelKey}_band`}
                />
                <Line
                  type="monotone"
                  dataKey={modelKey}
                  stroke={color}
                  strokeWidth={2.5}
                  dot={false}
                  name={modelKey}
                />
              </g>
            );
          })}
        </ComposedChart>
      </ResponsiveContainer>

      {/* Footer note */}
      <p className="mt-4 text-sm text-slate text-center italic">
        Shaded areas show 95% confidence intervals • Hover for detailed
        uncertainty at each point • {evolution.totalVotes} total votes •
        Snapshot every {evolution.interval} votes
      </p>
    </div>
  );
}
